import type { BoardQuery, Stage } from "@salesboard/contracts";
import { splitValues } from "@/hooks/useFilterSet";

/**
 * "Open", as configuration says it is **right now**.
 *
 * The `status` filter is gone, so "every open opportunity" is expressed the way the filter
 * panel expresses it: the set of active open phases. Computed from the stage list at the
 * moment it is needed rather than stored anywhere — an administrator who adds a phase must
 * not silently orphan the meaning of a tile or a profile's pipeline. A bookmarked URL keeps
 * the concrete ids it applied, which is honest about what it filters, while every fresh read
 * reflects today's configuration.
 *
 * Inactive phases are excluded because the board does not render them; a closed-type phase is
 * excluded because "open" is precisely what it is not.
 */
export function openStageIds(stages: Stage[] | undefined): string[] {
  return (stages ?? []).filter((stage) => stage.active && stage.closedType === "open").map((stage) => stage.id);
}

/**
 * The shared filter set, as the typed request every pipeline surface sends.
 *
 * `apiParams` comes out of `useFilterSet` already in the API's vocabulary, but it is
 * comma-joined strings — which is what a URL and a query string carry. The contract types
 * the four select filters as **arrays**, so exactly one function turns one into the other.
 *
 * One function rather than three, deliberately. The board's request builder and the analytics
 * request builder each used to enumerate the filters by hand, and that is precisely how the
 * board came to send a query key with `q` in it and a query string without: an enumerated
 * list is a list somebody forgets to extend. Now a filter added to `opportunityFilterDefs`
 * reaches the board, the list and analytics through here, and a filter removed from the
 * contract fails to compile in one place.
 *
 * `year` is not here: it is a pipeline **scope**, applied by each caller from
 * `usePipelineScope`, never a filter and never a chip.
 */
export function pipelineFilterQuery(apiParams: Record<string, string>): Omit<BoardQuery, "year"> {
  const list = (raw: string | undefined): string[] | undefined => {
    const values = splitValues(raw);
    return values.length > 0 ? values : undefined;
  };

  const owner = list(apiParams.owner);
  const client = list(apiParams.client);
  const stage = list(apiParams.stage);
  // Quarters are numbers in the contract; anything else in the URL is somebody's typo and is
  // dropped rather than sent for the server to reject — the rest of the filters still apply.
  const quarter = list(apiParams.quarter)
    ?.map(Number)
    .filter((value) => Number.isInteger(value) && value >= 1 && value <= 4);

  return {
    ...(owner ? { owner } : {}),
    ...(client ? { client } : {}),
    ...(stage ? { stage } : {}),
    ...(quarter && quarter.length > 0 ? { quarter } : {}),
    // The one filter with no panel control: the Stalled tile's landing site. Only ever "on".
    ...(apiParams.stalled === "true" ? { stalled: "true" as const } : {}),
    ...(apiParams.q ? { q: apiParams.q } : {}),
  };
}
